import { getStoredToken, storeToken, clearToken } from './auth';

const API_BASE: string = import.meta.env.VITE_API_URL || '';

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: string };
export type ApiFetchResult<T> = ApiOk<T> | ApiErr;

function normalizeResponse<T>(body: Record<string, unknown>): ApiFetchResult<T> {
  // Normalize Zod validation errors (format: { success: false, error: { issues: [...] } })
  if (!body.ok && body.success === false && typeof body.error === 'object' && body.error !== null) {
    const zodError = body.error as { issues?: Array<{ message?: string; path?: string[] }> };
    const msg = zodError.issues?.[0]?.message ?? 'Validation error';
    return { ok: false, error: msg };
  }
  return body as ApiFetchResult<T>;
}

async function tryRefresh(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const body = await res.json();
    if (!body.ok || !body.data?.access_token) return null;
    storeToken(body.data.access_token);
    return body.data.access_token;
  } catch {
    return null;
  }
}

export async function apiFetch<T>(
  url: string,
  options: RequestInit = {},
): Promise<ApiFetchResult<T>> {
  try {
    const token = getStoredToken();
    const headers = new Headers(options.headers);
    if (options.body) headers.set('Content-Type', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);

    let res = await fetch(`${API_BASE}${url}`, { ...options, headers, credentials: 'include' });

    if (res.status === 401) {
      const newToken = await tryRefresh();
      if (newToken) {
        headers.set('Authorization', `Bearer ${newToken}`);
        res = await fetch(`${API_BASE}${url}`, { ...options, headers, credentials: 'include' });
      } else {
        clearToken();
        window.location.href = '/login';
        return { ok: false, error: 'Session expired' };
      }
    }

    const body = await res.json() as Record<string, unknown>;
    return normalizeResponse<T>(body);
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'Network error' };
  }
}

export async function apiUpload<T>(
  url: string,
  formData: FormData,
): Promise<ApiFetchResult<T>> {
  try {
    const token = getStoredToken();
    const headers = new Headers();
    // Do NOT set Content-Type — let the browser set multipart boundary
    if (token) headers.set('Authorization', `Bearer ${token}`);

    let res = await fetch(`${API_BASE}${url}`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    if (res.status === 401) {
      const newToken = await tryRefresh();
      if (newToken) {
        headers.set('Authorization', `Bearer ${newToken}`);
        res = await fetch(`${API_BASE}${url}`, {
          method: 'POST',
          headers,
          body: formData,
          credentials: 'include',
        });
      } else {
        clearToken();
        window.location.href = '/login';
        return { ok: false, error: 'Session expired' };
      }
    }

    const body = await res.json() as Record<string, unknown>;
    return normalizeResponse<T>(body);
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'Network error' };
  }
}
